import { useCallback } from "react";
import {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  CellEditRequestEvent,
  GridReadyEvent,
  GetRowIdParams,
  UndoStartedEvent,
  UndoEndedEvent,
  RedoStartedEvent,
  RedoEndedEvent,
  CutStartEvent,
  CutEndEvent,
  PasteStartEvent,
  PasteEndEvent,
  CellSelectionDeleteStartEvent,
  CellSelectionDeleteEndEvent,
} from "ag-grid-community";

import { SheetStore } from "../sheet-store";
import { SheetRowData } from "../types";
import { useLatestRef } from "../utils";

interface UseTableHandlersProps<T extends SheetRowData> {
  store: SheetStore;
  onCellValueChanged?: (event: CellValueChangedEvent<T>) => void;
  onCellEditRequest?: (event: CellEditRequestEvent<T>) => void;
  onCellEditingStarted?: (event: CellEditingStartedEvent<T>) => void;
  onCellEditingStopped?: (event: CellEditingStoppedEvent<T>) => void;
  onUndoStarted?: (event: UndoStartedEvent<T>) => void;
  onUndoEnded?: (event: UndoEndedEvent<T>) => void;
  onRedoStarted?: (event: RedoStartedEvent<T>) => void;
  onRedoEnded?: (event: RedoEndedEvent<T>) => void;
  onCutStart?: (event: CutStartEvent<T>) => void;
  onCutEnd?: (event: CutEndEvent<T>) => void;
  onPasteStart?: (event: PasteStartEvent<T>) => void;
  onPasteEnd?: (event: PasteEndEvent<T>) => void;
  onCellSelectionDeleteStart?: (event: CellSelectionDeleteStartEvent<T>) => void;
  onCellSelectionDeleteEnd?: (event: CellSelectionDeleteEndEvent<T>) => void;
  onGridReady?: (event: GridReadyEvent<T>) => void;
  getRowId?: (params: GetRowIdParams<T>) => string;
}

/**
 * Bridges ag-grid events to the MobX store and forwards them to consumer
 * callbacks. Uses stable refs for consumer callbacks to prevent handler
 * recreation when consumers pass inline functions.
 */
export const useTableHandlers = <T extends SheetRowData>({
  store,
  onCellValueChanged,
  onCellEditRequest,
  onCellEditingStarted,
  onCellEditingStopped,
  onUndoStarted,
  onUndoEnded,
  onRedoStarted,
  onRedoEnded,
  onCutStart,
  onCutEnd,
  onPasteStart,
  onPasteEnd,
  onCellSelectionDeleteStart,
  onCellSelectionDeleteEnd,
  onGridReady,
  getRowId,
}: UseTableHandlersProps<T>) => {
  const onCellValueChangedRef = useLatestRef(onCellValueChanged);
  const onCellEditRequestRef = useLatestRef(onCellEditRequest);
  const onCellEditingStartedRef = useLatestRef(onCellEditingStarted);
  const onCellEditingStoppedRef = useLatestRef(onCellEditingStopped);
  const onUndoStartedRef = useLatestRef(onUndoStarted);
  const onUndoEndedRef = useLatestRef(onUndoEnded);
  const onRedoStartedRef = useLatestRef(onRedoStarted);
  const onRedoEndedRef = useLatestRef(onRedoEnded);
  const onCutStartRef = useLatestRef(onCutStart);
  const onCutEndRef = useLatestRef(onCutEnd);
  const onPasteStartRef = useLatestRef(onPasteStart);
  const onPasteEndRef = useLatestRef(onPasteEnd);
  const onCellSelectionDeleteStartRef = useLatestRef(onCellSelectionDeleteStart);
  const onCellSelectionDeleteEndRef = useLatestRef(onCellSelectionDeleteEnd);
  const onGridReadyRef = useLatestRef(onGridReady);
  const getRowIdRef = useLatestRef(getRowId);

  const getRowIdCallback = useCallback(
    (params: GetRowIdParams<T>) => {
      if (getRowIdRef.current) {
        return getRowIdRef.current(params);
      }
      return String(params.data.id);
    },
    [getRowIdRef],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent<T>) => {
      store.setGridApi(event.api);
      store.syncUndoRedoCounts();
      onGridReadyRef.current?.(event);
    },
    [store, onGridReadyRef],
  );

  const handleCellEditingStarted = useCallback(
    (event: CellEditingStartedEvent<T>) => {
      const rowId = event.data ? String(event.data.id) : null;
      const colId = event.column?.getColId() ?? null;
      store.setEditing(rowId, colId);
      onCellEditingStartedRef.current?.(event);
    },
    [store, onCellEditingStartedRef],
  );

  const handleCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<T>) => {
      store.clearEditing();
      onCellEditingStoppedRef.current?.(event);
    },
    [store, onCellEditingStoppedRef],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<T>) => {
      // During undo/redo, AG Grid fires intermediate cellValueChanged events
      // (one per reverted/re-applied cell). Skip count-syncing and dirty-tracking
      // here — the final sync happens in handleUndoEnded / handleRedoEnded.
      if (!store.isUndoRedoInProgress) {
        if (event.data) {
          store.markRowDirty(String(event.data.id));
        }
        store.syncUndoRedoCounts();
      }
      onCellValueChangedRef.current?.(event);
    },
    [store, onCellValueChangedRef],
  );

  const handleCellEditRequest = useCallback(
    (event: CellEditRequestEvent<T>) => {
      // Note: In readOnly mode, AG Grid does not perform undo/redo operations,
      // so syncing counts here would be misleading — counts never change.
      onCellEditRequestRef.current?.(event);
    },
    [onCellEditRequestRef],
  );

  const handleUndoStarted = useCallback(
    (event: UndoStartedEvent<T>) => {
      store.setUndoing(true);
      onUndoStartedRef.current?.(event);
    },
    [store, onUndoStartedRef],
  );

  const handleUndoEnded = useCallback(
    (event: UndoEndedEvent<T>) => {
      store.setUndoing(false);
      store.syncUndoRedoCounts();
      onUndoEndedRef.current?.(event);
    },
    [store, onUndoEndedRef],
  );

  const handleRedoStarted = useCallback(
    (event: RedoStartedEvent<T>) => {
      store.setRedoing(true);
      onRedoStartedRef.current?.(event);
    },
    [store, onRedoStartedRef],
  );

  const handleRedoEnded = useCallback(
    (event: RedoEndedEvent<T>) => {
      store.setRedoing(false);
      store.syncUndoRedoCounts();
      onRedoEndedRef.current?.(event);
    },
    [store, onRedoEndedRef],
  );

  const handleCutStart = useCallback(
    (event: CutStartEvent<T>) => {
      store.setCutting(true);
      onCutStartRef.current?.(event);
    },
    [store, onCutStartRef],
  );

  const handleCutEnd = useCallback(
    (event: CutEndEvent<T>) => {
      store.setCutting(false);
      store.syncUndoRedoCounts();
      onCutEndRef.current?.(event);
    },
    [store, onCutEndRef],
  );

  const handlePasteStart = useCallback(
    (event: PasteStartEvent<T>) => {
      store.setPasting(true);
      onPasteStartRef.current?.(event);
    },
    [store, onPasteStartRef],
  );

  const handlePasteEnd = useCallback(
    (event: PasteEndEvent<T>) => {
      store.setPasting(false);
      store.syncUndoRedoCounts();
      onPasteEndRef.current?.(event);
    },
    [store, onPasteEndRef],
  );

  const handleCellSelectionDeleteStart = useCallback(
    (event: CellSelectionDeleteStartEvent<T>) => {
      onCellSelectionDeleteStartRef.current?.(event);
    },
    [onCellSelectionDeleteStartRef],
  );

  const handleCellSelectionDeleteEnd = useCallback(
    (event: CellSelectionDeleteEndEvent<T>) => {
      store.syncUndoRedoCounts();
      onCellSelectionDeleteEndRef.current?.(event);
    },
    [store, onCellSelectionDeleteEndRef],
  );

  return {
    getRowIdCallback,
    handleGridReady,
    handleCellEditingStarted,
    handleCellEditingStopped,
    handleCellValueChanged,
    handleCellEditRequest,
    handleUndoStarted,
    handleUndoEnded,
    handleRedoStarted,
    handleRedoEnded,
    handleCutStart,
    handleCutEnd,
    handlePasteStart,
    handlePasteEnd,
    handleCellSelectionDeleteStart,
    handleCellSelectionDeleteEnd,
  };
};
